import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useReportData } from "./UseReportData";
import { DrillTree } from "./ProductionSection";
import { crewMetrics } from "./Crewsection";
import { ChartCard, Empty, KpiBand, PlanActualChart, Seg, SectionFrame, SimpleTable, StatusDot, StatusKey, bucketLabel, bucketTitle, cumsum, fmt, fmtPct, fmtT, statusOf, sumAgg, timeMetrics, varPct } from "./Reportkit";

const OZ = 31.1035;

function MiniStat({ title, rows, note }) {
  return (
    <ChartCard title={title}>
      {rows.map(([k, v]) => (
        <Box key={k} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.6, borderBottom: "1px dashed", borderColor: "divider", fontSize: 13 }}>
          <span style={{ opacity: 0.75 }}>{k}</span><b>{v}</b>
        </Box>
      ))}
      {note && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>{note}</Typography>}
    </ChartCard>
  );
}

function Body({ prod, eq, ev, sf, fuel, crew }) {
  const [mode, setMode] = useState("period");
  const t = prod.totals;
  const sr = t.oreActual ? t.wasteActual / t.oreActual : null, srP = t.orePlan ? t.wastePlan / t.orePlan : null;
  const head = t.auT ? t.auG / t.auT : null, headP = t.planAuT ? t.planAuG / t.planAuT : null;

  const trend = useMemo(() => {
    const a = prod.trend.map((x) => x.actual), p = prod.trend.map((x) => x.plan);
    const A = mode === "cum" ? cumsum(a) : a, P = mode === "cum" ? cumsum(p) : p;
    return prod.trend.map((x, i) => ({ label: bucketLabel(x.bucket, prod.grain), title: bucketTitle(x.bucket, prod.grain), actual: A[i], plan: P[i] }));
  }, [prod, mode]);

  // equipment matrix: haul trucks (the fleet with actual hours) vs loading units (plan only)
  const fam = (name) => { const rows = eq.byModel.filter((m) => m.family === name); return rows.length ? sumAgg(rows.map((m) => m.agg)) : null; };
  const trucks = fam("HAUL TRUCK"), shovels = fam("LOADING UNIT");
  const mt = trucks && timeMetrics(trucks), mtP = trucks && timeMetrics(trucks, true), ms = shovels && timeMetrics(shovels, true);
  const lines = [["Physical availability", "pa", "%", "higher"], ["Use of availability", "ua", "%", "higher"], ["Operating efficiency", "oe", "%", "higher"],
    ["Calendar hours", "cal", " h"], ["GOH", "goh", " h", "higher"], ["NOH", "noh", " h", "higher"], ["Delay hours", "delay", " h", "lower"], ["Standby hours", "standby", " h", "lower"], ["Breakdown hours", "bd", " h", "lower"]]
    .map(([label, k, u, b]) => ({ label, k, u, b }));
  const f1 = (v, u) => (v == null ? "—" : `${fmt(v, u === "%" ? 1 : 0)}${u}`);

  const k = ev.kpis, p = ev.previousKpis;
  const compliance = sf.attendances ? (sf.flhaCompliant / sf.attendances) * 100 : null;
  const lpt = fuel.totals.tonnes ? fuel.totals.truckLitres / fuel.totals.tonnes : null;
  const lptP = fuel.previousTotals.tonnes ? fuel.previousTotals.truckLitres / fuel.previousTotals.tonnes : null;

  return (
    <>
      <KpiBand items={[
        { label: "Total material moved", value: fmtT(t.actual), sub: `Plan ${fmtT(t.plan)}`, status: statusOf(t.actual, t.plan), delta: varPct(t.actual, t.plan) },
        { label: "Ore mined", value: fmtT(t.oreActual), sub: `Plan ${fmtT(t.orePlan)}`, status: statusOf(t.oreActual, t.orePlan), delta: varPct(t.oreActual, t.orePlan) },
        { label: "Waste moved", value: fmtT(t.wasteActual), sub: `Plan ${fmtT(t.wastePlan)}`, status: statusOf(t.wasteActual, t.wastePlan), delta: varPct(t.wasteActual, t.wastePlan) },
        { label: "Strip ratio", value: fmt(sr, 2), sub: `Plan ${fmt(srP, 2)}`, status: statusOf(sr, srP, "lower") },
        { label: "Head grade Au", value: head == null ? "—" : `${fmt(head, 2)} g/t`, sub: `Plan ${fmt(headP, 2)}`, status: statusOf(head, headP), delta: varPct(head, headP) },
        { label: "Contained gold", value: `${fmt(t.auG / OZ)} oz`, sub: `Plan ${fmt(t.planAuG / OZ)} oz`, status: statusOf(t.auG, t.planAuG), delta: varPct(t.auG, t.planAuG) },
        { label: "Trips", value: fmt(t.trips), sub: t.trips ? `${fmt(t.actual / t.trips, 1)} t / trip` : "" },
      ]} />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "7fr 5fr" }, gap: 2 }}>
        <DrillTree routes={prod.routes} />
        <ChartCard title="Total material moved — plan vs actual" sub={`By ${prod.grain}`} action={<Seg value={mode} onChange={setMode} options={[["period", "Per period"], ["cum", "Cumulative"]]} />}>
          <PlanActualChart data={trend} valueFmt={fmtT} height={330} />
          <StatusKey />
        </ChartCard>
      </Box>

      <ChartCard title="Equipment — plan vs actual" sub="Haul trucks have recorded hours; loading units are plan only">
        {!trucks && !shovels ? <Empty /> : (
          <SimpleTable max={440} cols={[
            { key: "label", label: "Metric", align: "left" },
            { key: "tp", label: "Trucks plan" }, { key: "ta", label: "Trucks actual", render: (r) => <b>{r.ta}</b> }, { key: "ts", label: "", render: (r) => r.st },
            { key: "sp", label: "Loading units plan" },
          ]} rows={lines.map((l) => ({
            label: l.label,
            tp: mtP ? f1(mtP[l.k], l.u) : "—", ta: mt && mt.cal ? f1(mt[l.k], l.u) : "no actual",
            st: mt && mt.cal && l.b ? <StatusDot status={statusOf(mt[l.k], mtP[l.k], l.b)} size={13} /> : null,
            sp: ms ? f1(ms[l.k], l.u) : "—",
          }))} />
        )}
      </ChartCard>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 2 }}>
        <MiniStat title="Hazards (FLHA)" rows={[["Shift attendances", fmt(sf.attendances)], ["With an FLHA", fmt(sf.flhaCompliant)], ["Compliance", fmtPct(compliance, 1)], ["Checklist defects", fmt(sf.defects)]]} />
        <MiniStat title="Breakdowns" rows={[["Events (closed)", fmt(k.breakdownCount)], ["Unplanned / planned", `${fmt(k.unplannedCount)} / ${fmt(k.breakdownCount - k.unplannedCount)}`], ["Unplanned downtime", `${fmt(k.unplannedHours, 0)} h`], ["Previous period", `${fmt(p.unplannedHours, 0)} h`], ["Units affected", fmt(k.unitsAffected)]]} />
        <MiniStat title="Non-productive time" rows={[["NPT reports", fmt(k.nptCount)], ["Hours logged", `${fmt(k.nptHours, 0)} h`], ["Previous period", `${fmt(p.nptHours, 0)} h`], ["Top reason", ev.nptReasons[0] ? `${ev.nptReasons[0].reason} (${fmt(ev.nptReasons[0].hours, 0)} h)` : "—"]]} />
        <MiniStat title="Fuel" rows={[["Dispensed", `${fmt(fuel.totals.litres / 1000, 1)} kL`], ["Litres per tonne", lpt == null ? "—" : fmt(lpt, 3)], ["Previous period", lptP == null ? "—" : fmt(lptP, 3)], ["Entries", fmt(fuel.totals.entries)]]} note="Status for fuel and events compares with the previous period." />
      </Box>

      <ChartCard title="Crew scorecard" sub="Crew = crew on the dispatch shift">
        <SimpleTable max={300} cols={[
          { key: "crew", label: "Crew", align: "left", render: (r) => <b>Crew {r.crew}</b> }, { key: "shifts", label: "Shifts", render: (r) => fmt(r.shifts) },
          { key: "plan", label: "Plan", render: (r) => fmtT(r.plan) }, { key: "actual", label: "Actual", render: (r) => <b>{fmtT(r.actual)}</b> },
          { key: "v", label: "Var %", render: (r) => <>{fmtPct(varPct(r.actual, r.plan), 1, true)} <StatusDot status={statusOf(r.actual, r.plan)} size={12} /></> },
          { key: "ps", label: "t / shift", render: (r) => fmtT(crewMetrics(r).perShift) }, { key: "cy", label: "Cycle min", render: (r) => fmt(crewMetrics(r).cycle, 1) },
          { key: "bd", label: "Unplanned BD h", render: (r) => fmt(r.unplannedHours, 1) }, { key: "fl", label: "FLHA", render: (r) => fmtPct(crewMetrics(r).compliance, 0) },
        ]} rows={crew.crews} />
      </ChartCard>
    </>
  );
}

export default function ExecutiveSection({ req }) {
  const prod = useReportData("production", req);
  const eq = useReportData("equipment", req);
  const ev = useReportData("events", req);
  const sf = useReportData("safety", req);
  const fuel = useReportData("fuel", req);
  const crew = useReportData("crew", req);
  const all = [prod, eq, ev, sf, fuel, crew];
  const ready = all.every((s) => s.data);
  const state = { data: ready ? true : null, loading: all.some((s) => s.loading), error: all.find((s) => s.error)?.error || null, reload: () => all.forEach((s) => s.reload()) };
  return (
    <SectionFrame eyebrow="For managers & directors" title="Executive Summary" desc="Production, equipment, safety, events, fuel and crews on one page" state={state}>
      {ready && <Body prod={prod.data} eq={eq.data} ev={ev.data} sf={sf.data} fuel={fuel.data} crew={crew.data} />}
    </SectionFrame>
  );
}